import React from 'react';

/**
 * The display panel's contents — the black instrument at the top of the page.
 *
 * ONE component, rendered by both the inline and the fullscreen branch of
 * `CountdownTimer`. Those two were near-duplicate markup, which is the exact shape of
 * the "second surface reads different data" defect the running-state plan records twice
 * (§9.1 web hero vs list, §9.2 iOS landscape hero). Size is a prop; content is not.
 *
 * Planned mode renders the cue block alone — no NOW strip, no run strip — so a timeline
 * that is not being operated looks exactly as it did before Operate Mode existed.
 */

export type PanelScale = 'inline' | 'fullscreen';

export interface NowStripModel {
  /** What is happening right now. */
  description: string;
  /** Seconds elapsed in the element in progress. */
  elapsed: string;
  /** Its planned length. */
  budget: string;
  /** 0..1, clamped. */
  progress: number;
  /** True once elapsed has passed budget — the bar and figures go amber. */
  over: boolean;
}

export interface RunStripModel {
  /** Unsigned magnitude, e.g. `1:10`. Empty when on time. */
  magnitude: string;
  /** `behind` | `ahead` | `on time`. Never dropped — this is the non-colour channel. */
  word: string;
  /** ▲ ▼ ● — the third channel, so the readout survives greyscale. */
  glyph: string;
  tone: 'behind' | 'ahead' | 'on-time';
  /** The next element that cannot move, and when. The deadline actually being managed. */
  nextFixed: { description: string; atClock: string } | null;
}

export interface CueModel {
  /** The element being cued, or a status phrase outside a run. */
  description: string;
  /** `CUE IN`, `OVERDUE BY`, `Starts in`, … */
  label: string;
  /** The primary readout. */
  value: string;
  /** Wall-clock time the cue lands, e.g. `11:59:10 AM`. Null outside a run. */
  atClock: string | null;
  /** Show the label + value pair at all. */
  showCountdown: boolean;
  /** Render the big digits in the overdue colour. */
  isOverdue: boolean;
}

interface DisplayPanelProps {
  scale: PanelScale;
  cue: CueModel;
  now?: NowStripModel | null;
  run?: RunStripModel | null;
  /** Colour class for the primary readout, owned by the parent. */
  valueColorClass: string;
  /** Rendered instead of the countdown when the occurrence is simply underway. */
  showHappeningNow: boolean;
}

const TONE_TEXT: Record<RunStripModel['tone'], string> = {
  behind: 'text-amber-300',
  ahead: 'text-sky-300',
  'on-time': 'text-emerald-300',
};

const DisplayPanel: React.FC<DisplayPanelProps> = ({
  scale,
  cue,
  now = null,
  run = null,
  valueColorClass,
  showHappeningNow,
}) => {
  const big = scale === 'fullscreen';

  const cls = {
    strip: big ? 'text-2xl' : 'text-xs sm:text-sm',
    stripGap: big ? 'gap-6 px-2 pb-8' : 'gap-2 sm:gap-4 pb-3 sm:pb-4',
    runGap: big ? 'gap-6 px-2 pt-8' : 'gap-2 sm:gap-4 pt-3 sm:pt-4',
    description: big
      ? 'text-4xl mb-6'
      : 'text-xl sm:text-2xl md:text-3xl mb-3',
    label: big
      ? 'text-3xl mb-6'
      : 'text-base sm:text-lg md:text-xl mb-3',
    value: big
      ? 'text-9xl'
      : 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl',
    atClock: big ? 'text-2xl mt-6' : 'text-sm sm:text-base mt-2 sm:mt-3',
    happening: big
      ? 'text-5xl'
      : 'text-2xl sm:text-3xl md:text-4xl',
    bar: big ? 'h-3' : 'h-1.5',
  };

  return (
    <div className="w-full">
      {/* ---- NOW strip: what IS happening. Absent outside a run. ---- */}
      {now && (
        <div
          className={`flex flex-wrap items-center justify-center ${cls.stripGap} ${cls.strip} border-b border-white/10 mb-4 sm:mb-6 w-full`}
        >
          <span className="uppercase tracking-[0.18em] text-white/40 font-medium">Now</span>
          <>
              <span className="text-white font-semibold truncate max-w-[16ch] sm:max-w-none">
                {now.description}
              </span>
              <span className={`font-mono tabular-nums ${now.over ? 'text-amber-300' : 'text-white/70'}`}>
                {now.elapsed} <span className="text-white/35">of</span> {now.budget}
              </span>
              <span
                className={`${cls.bar} ${big ? 'w-64' : 'w-20 sm:w-32'} bg-white/15 rounded-full overflow-hidden shrink-0`}
                role="presentation"
              >
                <span
                  className={`block h-full rounded-full transition-[width] duration-700 ease-out ${
                    now.over ? 'bg-amber-400' : 'bg-white/70'
                  }`}
                  style={{ width: `${Math.round(Math.min(1, Math.max(0, now.progress)) * 100)}%` }}
                />
              </span>
          </>
        </div>
      )}

      {/* ---- The cue: what to do next, and when. ---- */}
      <div className={`text-white font-bold transition-all duration-300 ${cls.description}`}>
        {cue.description}
      </div>

      {cue.showCountdown ? (
        <>
          <div
            className={`text-white/70 font-medium transition-all duration-300 tracking-[0.28em] ${cls.label}`}
          >
            {cue.label}
          </div>
          <div
            className={`font-bold font-mono tabular-nums tracking-tight drop-shadow-lg transition-all duration-300 ${cls.value} ${valueColorClass}`}
          >
            {cue.value}
          </div>
          {cue.atClock && (
            <div className={`text-white/45 font-medium tabular-nums ${cls.atClock}`}>
              at {cue.atClock}
            </div>
          )}
        </>
      ) : showHappeningNow ? (
        <div className={`font-bold text-state-active transition-all duration-300 ${cls.happening}`}>
          Happening Now
        </div>
      ) : null}

      {/* ---- Run strip: how the run is tracking, and the next immovable deadline. ---- */}
      {run && (
        <div
          className={`flex flex-wrap items-center justify-center ${cls.runGap} ${cls.strip} border-t border-white/10 mt-4 sm:mt-6 w-full`}
        >
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" role="presentation" />
            <span className="uppercase tracking-[0.18em] text-white/40 font-medium">Running</span>
          </span>
          <span className={`font-semibold tabular-nums ${TONE_TEXT[run.tone]}`}>
            <span aria-hidden="true">{run.glyph} </span>
            {run.magnitude ? `${run.magnitude} ${run.word}` : 'On time'}
          </span>
          {run.nextFixed && (
            <span className="text-white/45">
              <span className="uppercase tracking-[0.14em] text-white/30 mr-2">next fixed</span>
              <span className="text-white/70 font-medium">{run.nextFixed.description}</span>
              <span className="ml-2 tabular-nums">{run.nextFixed.atClock}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DisplayPanel;
